import { useSettings } from "@/context/SettingsContext";

// ── Single source of truth for editable business/marketing copy ──────────────
// These are ONLY fallback defaults. The live values are stored in the existing
// settings document under `content.*` (edited via Admin → Content Manager) and
// surfaced through GET /api/settings. Storefront reads via useContent() below.
export const CONTENT_DEFAULTS = {
  // Checkout & Payments
  checkout_subtitle:
    "Tell us where to send it and we'll confirm on WhatsApp. Payment details are shared in chat.",
  payment_value_note:
    "Simple payments. Fairer prices. We avoid unnecessary payment processing costs so we can keep more value in your gift.",

  // Packaging & Shipping ({shipping_charge} is replaced live with the Admin amount)
  shipping_note:
    "Shipping {shipping_charge}. An additional delivery charge may apply to remote locations and will be confirmed before payment.",
  standard_packaging_desc: "Our lovely gift-ready packaging with a handwritten note.",
  signature_packaging_desc: "Our premium branded presentation box.",

  // Build Your Parcel
  builder_title: "Hand-pick your perfect parcel",
  builder_subtitle:
    "Choose the cute accessories you love — we'll pack them beautifully and gift-ready.",
  builder_spend_guide_note: "A gentle guide to help you choose — add as many pieces as you like.",

  // Homepage — Special gifting section
  special_gifting_title: "Two special ways to gift",
  special_gifting_subtitle:
    "Beyond our curated parcels — thoughtful options for teams and one-of-a-kind keepsakes.",
  corporate_card_title: "Thoughtful gifts for your team",
  corporate_card_copy:
    "Curated gifts for employees, clients and special occasions — tailored to your budget and quantity.",
  silver_card_title: "A gift made for one",
  silver_card_copy:
    "Turn a thought, memory or idea into a custom silver creation, designed especially for someone you love.",

  // Footer / General brand copy
  footer_brand_copy:
    "Thoughtfully curated, beautifully packed gift parcels of meaningful keepsakes & cute fashion accessories — ready to surprise someone special.",

  // Personalisation (Make It Personal)
  personalize_heading: "Turn your gift into a moment",
  personalize_subcopy:
    "Add a photo, record your voice, and share a heartfelt note — we'll wrap it all into a private gift page they can open with a simple scan.",
  personalize_surprise_note:
    "You create the surprise here. We'll place it inside their parcel for them to discover when they open their gift — your note, photo and voice message open through the special card & QR tucked inside.",

  // Optional Personalisation Explainer (independently toggleable visual)
  personalization_explainer: {
    enabled: true,
    heading: "How your surprise reaches them",
    steps: [
      { title: "You create the surprise", copy: "Add your message, photo or voice note." },
      { title: "We pack it inside the gift", copy: "We turn it into a private little surprise inside their parcel." },
      { title: "They discover it", copy: "They scan the card and discover what you created just for them." },
    ],
  },
};

// Merge stored content over defaults (nested explainer handled carefully).
export function mergeContent(stored) {
  const c = stored || {};
  const ex = c.personalization_explainer || {};
  const steps = Array.isArray(ex.steps) && ex.steps.length ? ex.steps : CONTENT_DEFAULTS.personalization_explainer.steps;
  return {
    ...CONTENT_DEFAULTS,
    ...c,
    personalization_explainer: {
      ...CONTENT_DEFAULTS.personalization_explainer,
      ...ex,
      steps,
    },
  };
}

// Storefront hook — reads editable copy with graceful fallback to defaults.
export function useContent() {
  const settings = useSettings();
  return mergeContent(settings?.content);
}

// Replace {shipping_charge} (and similar) placeholders with live commerce values.
export function fillPlaceholders(text, vars = {}) {
  if (!text) return "";
  return String(text).replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? String(vars[k]) : m));
}
